import { useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import type { Report } from "@coachbrain/contracts";
import { Approved, Card, PrimaryButton, SectionLabel, Unresolved, Num, Measured, Columns, Col } from "@coachbrain/ui";
import { CoachShell, ErrorBox, Loading, useMedia } from "../components/common";
import { useLoad } from "../lib/hooks";
import { useSession } from "../lib/session";
import { dateDayMonth } from "../lib/format";
import { en } from "../i18n/en";

/** The rendered report — identical on the coach preview and the trainee page (R-RPT-01: facts and signals only; no LayerChip here by design). */
export function ReportBody({ r, coachName, clientName }: { r: Report; coachName: string; clientName: string }) {
  const t = en.reports;
  const max = Math.max(...(r.series?.[0]?.points.map((p) => p.value) ?? [1]));
  return (
    <>
      <Card>
        <SectionLabel>{t.yourGoal}</SectionLabel>
        {r.goals.map((g, i) => <div key={i}><div className="cb-h3" style={{ fontSize: 19 }}>“{g.verbatim}”</div><div className="cb-row-sub">{g.progress}</div></div>)}
      </Card>
      <Card className="cb-card-flush">
        <div><SectionLabel>{t.compared(r.previousPeriod?.label)}</SectionLabel></div>
        {r.markers.map((m) => <div key={m.label} className="cb-row-main"><span className="cb-row-left" style={{ fontWeight: 600, fontSize: 15 }}>{m.label}</span><span><Num className="cb-row-right">{m.current}</Num>{m.previous ? <Measured className="cb-small" style={{ marginLeft: 8 }}>was {m.previous}</Measured> : null}</span></div>)}
      </Card>
      {r.series?.map((s) => (
        <Card key={s.label}>
          <SectionLabel>{s.label.toUpperCase()} · {s.unit.toUpperCase()}</SectionLabel>
          <div className="cb-trend-bars cb-report-bars" style={{ borderBottom: 0 }}>
            {s.points.map((p) => (
              <div key={p.label} className="cb-trend-col">
                <span className={`cb-num cb-trend-val ${p.goal ? "cb-text-emerald" : ""}`}>{p.value.toFixed(1)}</span>
                <span className={p.goal ? "" : "cb-trend-bar cb-bar-blue"} style={p.goal ? { width: "100%", maxWidth: 44, borderTop: "3px dashed var(--cb-emerald)", height: `${(p.value / max) * 100}%` } : { height: `${(p.value / max) * 100}%` }} />
                <span className="cb-section-label" style={{ marginTop: 6 }}>{p.label}</span>
              </div>
            ))}
          </div>
        </Card>
      ))}
      {r.milestones.map((m) => <Card key={m.text} tone="mint"><SectionLabel tone="emerald">{t.milestone}</SectionLabel><div className="cb-h3">{m.text} — reached {dateDayMonth(m.reachedAt)}</div></Card>)}
      <Card accent="emerald"><SectionLabel tone="emerald">{t.fromCoach}</SectionLabel><div className="cb-body">“{r.coachNote}”</div></Card>
      <p className="cb-sr-only">{clientName} · {coachName}</p>
    </>
  );
}

/** Reports: list + preview screens on phones; list left / rendered preview right from 768px. */
export function ReportsRoute() {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const desktop = useMedia("(min-width: 768px)");
  // Names come from data, never from the fixture: the client from the overview header, the coach from the session.
  const me = useSession();
  const { api, data: loaded, error, reload, setData: setLoaded } = useLoad((a) => Promise.all([a.reports(id), a.overview(id)]), [id]);
  const [preview, setPreview] = useState<Report | null>(null);
  const [note, setNote] = useState("");
  if (error) return <CoachShell><ErrorBox message={error} onRetry={reload} /></CoachShell>;
  if (!loaded || !api) return <CoachShell><Loading /></CoachShell>;
  const [data, overview] = loaded;
  const clientName = overview.header.name;
  const coachName = me?.user.name ?? "";
  const t = en.reports;
  const refresh = async () => setLoaded([await api.reports(id), overview]);
  const current = preview ?? (desktop ? data[0] ?? null : null);
  const actions = current ? (current.status !== "sent"
    ? <PrimaryButton tone="emerald" full onClick={async () => { const r = await api.sendReport(current.id); setPreview(r); await refresh(); }}>{t.send}</PrimaryButton>
    : <div><Approved>{t.sent}</Approved></div>) : null;
  const previewPane = current ? (
    <div className="cb-report-preview" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SectionLabel right={current.status === "sent" ? <Approved>{t.sent}</Approved> : <Unresolved>{t.held}</Unresolved>}>{t.progress} · {current.period.label}</SectionLabel>
      <ReportBody r={current} coachName={coachName} clientName={clientName} />
      {actions}
    </div>
  ) : <Card><span className="cb-muted">{t.pick}</span></Card>;
  const list = (
    <>
      <Card>
        <SectionLabel>{t.newReport}</SectionLabel>
        <div className="cb-field"><label htmlFor="note">{t.coachNote}</label><textarea id="note" className="cb-textarea" value={note} onChange={(e) => setNote(e.target.value)} /></div>
        <PrimaryButton tone="navy" full onClick={async () => { const r = await api.draftReport(id, { coachNote: note }); await refresh(); setPreview(r); }}>{t.draft}</PrimaryButton>
      </Card>
      <SectionLabel className="cb-page-label">{t.list}</SectionLabel>
      {data.map((r) => (
        <Card key={r.id} className={desktop && current?.id === r.id ? "cb-card-selected" : ""} onClick={() => setPreview(r)}>
          <div className="cb-row-main"><span className="cb-h3">{r.period.label}</span>{r.status === "sent" ? <Approved>{t.sent}</Approved> : <Unresolved>{t.held}</Unresolved>}</div>
          <span className="cb-small cb-muted">{r.coachNote.slice(0, 80)}{r.coachNote.length > 80 ? "…" : ""}</span>
        </Card>
      ))}
    </>
  );
  if (!desktop && preview) {
    return (
      <CoachShell title={`${t.progress} · ${preview.period.label}`} subtitle={preview.status === "sent" ? t.sent : t.held} right={<button type="button" className="cb-link-button" style={{ color: "#C9D2DE" }} onClick={() => setPreview(null)}>{en.common.back}</button>} hideNav>
        <ReportBody r={preview} coachName={coachName} clientName={clientName} />
        {actions}
      </CoachShell>
    );
  }
  return (
    <CoachShell wide title={t.title} right={<button type="button" className="cb-link-button" style={{ color: "#C9D2DE" }} onClick={() => void navigate({ to: `/clients/${id}` })}>{en.common.back}</button>}>
      {desktop ? <Columns kind="master-detail"><Col area="list">{list}</Col><Col area="detail">{previewPane}</Col></Columns> : list}
    </CoachShell>
  );
}
